<template>
  <div class="additive-widget pt-0">
    <div :class="[
        'form-wrapper my-4',
        showForm ? 'w-full' : 'hide-additive-form'
      ]"
    >
      <span class="empty-text mb-4">No {{ label }} have been added</span>
      <vue-form-generator
          class="w-full p-0"
          ref="additiveForm"
          :model="value"
          :options="formOptions"
          :schema="localSchema"
      />
    </div>
  </div>
</template>

<script>

import FormFieldValidationMixin from '../../mixins/FormFieldValidationMixin';
export default {
  props: {
    value: {
      type: Object,
      required: true
    },
    formOptions: {
      type: Object,
      required: true,
    },
    schema: {
      type: Object,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    primaryToggle: {
      type: Boolean,
      default: false
    },
    primaryFieldName: {
      type: String,
      default: 'id'
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    mandatory: {
      type: Boolean,
      required: false,
      default: false
    },
    maxCount: {
      type: Number,
      required: false,
      default: 0
    },
  },
  mixins: [FormFieldValidationMixin],
  data() {
    return {
      localSchema: {},
      groupModelName: null,
    };
  },
  watch: {
    value: {
      deep: true,
      handler() {
        this.checkModelCount();
      }
    }
  },
  computed: {
    showForm() {
      let showForm = false;

      this.localSchema.groups.forEach(group => {
        group.fields.forEach(field => {
          const model = this.value[field.model];
          if (model && Array.isArray(model) && model.length > 0) {
            showForm = true;
          }
        })
      })

      return showForm;
    }
  },
  methods: {
    checkModelCount() {
      this.localSchema.groups.forEach(group => {
        group.fields.forEach(field => {
          const model = this.value[field.model];

          // Check if model exists and is an array
          if (!model || !Array.isArray(model)) {
            return;
          }

          // If a single element, set as primary if it applies
          if (model.length === 1) {
            if (this.primaryFieldName && this.$refs.additiveForm) {
              const items = this.$refs.additiveForm.$parent.value[this.groupModelName];
              if (items && items.length > 0 && items[0]) {
                items[0][this.primaryFieldName] = 1;
              }
            }

            if (this.mandatory && this.$refs.additiveForm) {
              const removeButton = this.$refs.additiveForm.$el.querySelector('.remove-button');
              if (removeButton) {
                removeButton.style.display = 'none';
              }
            }
          } else if (this.$refs.additiveForm) {
            const removeButton = this.$refs.additiveForm.$el.querySelector('.remove-button');
              if (removeButton) {
                removeButton.style.display = '';
              }

            if (this.maxCount > 0 && model.length >= this.maxCount) {
              const addButton = this.$refs.additiveForm.$el.querySelector('.add-button');
              if (addButton) {
                addButton.style.display = 'none';
              }
            } else {
              const addButton = this.$refs.additiveForm.$el.querySelector('.add-button');
              if (addButton) {
                addButton.style.display = '';
              }
            }
          }
        });
      });
    },
    setPrimaryToggle() {
      if (!this.primaryToggle) {
        return true;
      }

      this.localSchema.groups.forEach(group => {
        group.fields.forEach(field => {
          this.groupModelName = field.model;

          field.items.schema.groups.unshift(
            {
              styleClasses: 'primary-toggle-row w-full content flex items-end',
              fields: [
                {
                  disabled: true,
                  type: 'input',
                  inputType: 'hidden',
                  styleClasses: 'hidden',
                  model: this.primaryFieldName
                },
                {
                  type: 'label',
                  visible: model => model && !parseInt(model[this.primaryFieldName]),
                  styleClasses: 'w-1/2 m-0 py-4 is-secondary-label',
                  label: 'Secondary'
                },
                {
                  type: 'pillButton',
                  styleClasses: 'w-1/2 m-0 py-4 set-primary-button',
                  buttonText: 'Set as primary',
                  fieldClasses: 'pointer',
                  onClick: model => {
                    // We first set all items to not be primary
                    const items = this.$refs.additiveForm.$parent.value[this.groupModelName];
                    items.forEach(item => item[this.primaryFieldName] = 0);

                    // Then, we set this one as the primary
                    model[this.primaryFieldName] = 1;

                    // Finally, we need to trigger a validation for the form to update
                    this.validateGroupedField(
                        this.primaryFieldName,
                        this.groupModelName,
                        'additiveForm'
                    );
                  },
                  visible: model => !this.readonly && model && !parseInt(model[this.primaryFieldName])
                },
                {
                  type: 'label',
                  visible: model => model && parseInt(model[this.primaryFieldName]),
                  styleClasses: 'w-1/2 m-0 py-4 is-secondary-label',
                  label: 'Primary',
                },
                {
                  type: 'pillButton',
                  styleClasses: 'w-1/2 m-0 py-4 is-primary-button flex',
                  buttonText: 'Primary',
                  buttonColor: 'success',
                  disabled: true,
                  fieldClasses: 'text-white',
                  visible: model => !this.readonly && model && parseInt(model[this.primaryFieldName])
                },
              ]
            }
          );
        })
      })
    },
  },
  created() {
    this.localSchema = this.schema;
    this.setPrimaryToggle();
    setTimeout(() => {
      this.checkModelCount();
    }, 1000);
  }
};
</script>

<style lang="scss">
.readonly-form {
  .additive-widget {
    .empty-text {
      margin: 0;
      padding: 1rem;
    }

    .form-array-editor {
      > div {
        > span {
          border: none !important;
          padding: .5rem 0 !important;

          .form-group {
            &.is-primary-label,
            &.is-secondary-label,
            &.is-primary-button,
            &.set-primary-button {
              padding: 1rem 0 !important;
            }
          }
        }
      }
    }

    .form-wrapper.hide-additive-form {
      .vue-form-generator {
        display: none;
      }
    }
  }
}

.additive-widget {
  padding-bottom: 0.1px;

  .form-wrapper {
    .empty-text {
      display: none;
    }

    .form-array-editor {
      > div {
        > span {
          border-right: 1px solid #D5DAE0;

          &:not(:only-child) {
            flex-grow: 0;
          }
        }

        > input {
          margin-left: auto !important;
          margin-right: auto !important;
        }
      }
    }

    &.hide-additive-form {
      border: 1px solid #D5DAE0;
      border-radius: 5px;
      margin: 1rem;
      text-align: center;

      .empty-text {
        display: block;
        padding-top: 2rem;
      }

      .vue-form-generator {
        padding-bottom: 2rem !important;

        fieldset {
          border: none !important;
          padding: 0 !important;

          .form-array-editor {
            > div {
              display: none !important;
            }
          }

          .add-button {
            margin: auto;
          }
        }
      }
    }

    .vue-form-generator {
      .form-group {
        margin-bottom: 0;
        padding-bottom: 0;
      }

      .field-array > .field-wrap > div > div {
        align-items: start !important;

        > span {
          width: 91.66667%; // 8.33333%  or w-1/12 is the size of the .remove-button input
        }
      }
    }
  }

  .crud-buttons {
    border-left: 1px solid #d5dae0;
  }
  .form-array-editor {
    > div {
      > span {
        padding-left: 0 !important;
        padding-right: 0 !important;
        padding-bottom: 1rem !important;

        .vue-form-generator {
          display: flex;
          flex-direction: row;
        }
      }
    }

    .primary-toggle-row {
      margin-top: -1rem;
      margin-bottom: 1rem;
      border-bottom: 1px solid #d5dae0;
    }

    .set-primary-button {
      span {
        color: #444b57;
      }
    }

    .is-secondary-label,
    .is-primary-label {
      span {
        font-size: 20px !important;
      }
    }

    .is-primary-button,
    .set-primary-button {
      display: flex;
      justify-content: flex-end;
    }

    .crud-buttons {
      border-left: 1px solid #d5dae0;
    }

    .phone_numbers_child_additive {
      padding: 0;
      margin: 0;

      #phone-numbers {
        > div {
          border: none;
          margin: 0;

          > span {
            padding: 0;

            .vue-form-generator {
              padding: 0;
            }

            .field-wrap {
              margin-top: 0.25rem;
            }
          }

          .field-input {
            padding: 0;
            padding-left: 1rem;
            margin: 0;
          }

          &:not(:first-child) {
            .field-label,
            label {
              display: none;
            }
          }

          &:last-child {
            margin-bottom: 1rem;
          }
        }
      }
    }
  }
}
</style>
